import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiUser } from '@/api';

// NOTE: Hono RPC typed client (api.auth.me.$get etc.) is NOT used here because
// buildRoutes() returns Hono<BlankEnv, BlankSchema> — route paths are not typed
// in the dist declarations. Falling back to plain fetch with credentials:include.

interface AuthState {
  user: ApiUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<ApiUser | null> => {
      const r = await fetch('/api/auth/me', { credentials: 'include' });
      if (r.status === 401) return null;
      if (!r.ok) throw new Error('me failed');
      return ((await r.json()) as { user: ApiUser }).user;
    },
    staleTime: 30_000,
  });

  const loginM = useMutation({
    mutationFn: async (vars: { username: string; password: string }) => {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(vars),
      });
      if (r.status === 401) throw new Error('Invalid credentials');
      if (!r.ok) throw new Error('Login failed');
      return ((await r.json()) as { user: ApiUser }).user;
    },
    onSuccess: (user) => qc.setQueryData(['me'], user),
  });

  const logoutM = useMutation({
    mutationFn: async () => {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    },
    onSuccess: () => {
      qc.setQueryData(['me'], null);
      qc.invalidateQueries();
    },
  });

  const value: AuthState = {
    user: meQ.data ?? null,
    isLoading: meQ.isLoading,
    login: async (u, p) => { await loginM.mutateAsync({ username: u, password: p }); },
    logout: async () => { await logoutM.mutateAsync(); },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuthCtx(): AuthState {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
